import { useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Card, Input, Select, Modal, StatusPill, EmptyState, useToast } from '../../../design-system';

const EMPTY = { accountNo: '', ifsc: '', bankName: '', branch: '', accountType: 'current', isPrimary: false };
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;

function BankAccountManager({ clientId, bankAccounts, onChange }) {
  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field, v) => setForm((f) => ({ ...f, [field]: v }));

  const openAdd = () => { setEditing(null); setForm(EMPTY); setError(''); setModalOpen(true); };
  const openEdit = (b) => {
    setEditing(b._id);
    setForm({ accountNo: b.accountNo || '', ifsc: b.ifsc || '', bankName: b.bankName || '', branch: b.branch || '', accountType: b.accountType || 'current', isPrimary: !!b.isPrimary });
    setError('');
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.accountNo.trim() || !form.ifsc.trim()) { setError('Account number and IFSC are required'); return; }
    if (!IFSC_RE.test(form.ifsc.trim().toUpperCase())) { setError('IFSC looks invalid (e.g. HDFC0001234)'); return; }
    setSaving(true);
    setError('');
    try {
      const res = editing
        ? await apiClient.put(`/api/clients/${clientId}/bank-accounts/${editing}`, form)
        : await apiClient.post(`/api/clients/${clientId}/bank-accounts`, form);
      onChange(res.data);
      setModalOpen(false);
      toast.success(editing ? 'Bank account updated' : 'Bank account added');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save bank account');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (accountId) => {
    if (!window.confirm('Remove this bank account?')) return;
    try {
      const res = await apiClient.delete(`/api/clients/${clientId}/bank-accounts/${accountId}`);
      onChange(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to remove bank account');
    }
  };

  return (
    <Card>
      <Card.Header title="Bank Accounts" action={<Button size="sm" onClick={openAdd}>+ Add Account</Button>} />
      {bankAccounts.length === 0 ? (
        <EmptyState icon="🏦" title="No bank accounts added" />
      ) : (
        <div className="gstins-list">
          {bankAccounts.map((b) => (
            <div key={b._id} className="gstin-card">
              <div className="gstin-info">
                <strong>{b.bankName || 'Bank'} · {b.accountNo}</strong>
                <span className="gstin-meta">{b.ifsc}</span>
                {b.branch && <span className="gstin-meta">{b.branch}</span>}
                <span className="gstin-meta">{b.accountType}</span>
                {b.isPrimary && <StatusPill variant="success" label="Primary" />}
              </div>
              <div className="enrollment-actions">
                <Button variant="secondary" size="sm" onClick={() => openEdit(b)}>Edit</Button>
                <button onClick={() => remove(b._id)} className="btn-remove">Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Bank Account' : 'Add Bank Account'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={saving}>{editing ? 'Save' : 'Add'}</Button>
          </>
        }
      >
        <form className="client-form" onSubmit={save}>
          {error && <div className="form-error" role="alert">{error}</div>}
          <div className="form-row">
            <Input label="Account Number *" value={form.accountNo} onChange={(e) => set('accountNo', e.target.value)} />
            <Input label="IFSC *" value={form.ifsc} onChange={(e) => set('ifsc', e.target.value.toUpperCase())} maxLength={11} />
          </div>
          <div className="form-row">
            <Input label="Bank Name" value={form.bankName} onChange={(e) => set('bankName', e.target.value)} />
            <Input label="Branch" value={form.branch} onChange={(e) => set('branch', e.target.value)} />
            <Select label="Account Type" value={form.accountType} onChange={(e) => set('accountType', e.target.value)}>
              <option value="savings">Savings</option>
              <option value="current">Current</option>
              <option value="cc">Cash Credit</option>
              <option value="od">Overdraft</option>
            </Select>
          </div>
          <label className="checkbox-label">
            <input type="checkbox" checked={form.isPrimary} onChange={(e) => set('isPrimary', e.target.checked)} />
            Primary account
          </label>
        </form>
      </Modal>
    </Card>
  );
}

export default BankAccountManager;
